import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, MapPin, Calendar, ArrowRight, Inbox } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";
import { ORG_TYPES, localize } from "../lib/meta";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { InitiativeEditor } from "../components/InitiativeEditor";
import { InquiryInbox } from "../components/InquiryInbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function OrgProfileCard() {
  const { t, lang } = useLang();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ org_name: user?.org_name || "", org_type: user?.org_type || "company", contact_name: user?.contact_name || "", org_website: user?.org_website || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch("/auth/settings", form); await refresh(); toast.success(t("organizer.org_profile_saved")); }
    catch { toast.error(t("organizer.org_profile_error")); }
    finally { setSaving(false); }
  };
  return (
    <div className="mb-8 rounded-2xl border border-border bg-white p-6" data-testid="org-profile-card">
      <h2 className="font-display text-lg font-semibold text-slateink">{t("organizer.org_profile_title")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("organizer.org_profile_desc")}</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><Label>{t("organizer.org_name_label")}</Label><Input value={form.org_name} onChange={(e) => setForm((f) => ({ ...f, org_name: e.target.value }))} data-testid="org-name-input" /></div>
        <div>
          <Label>{t("organizer.org_type_label")}</Label>
          <Select value={form.org_type} onValueChange={(v) => setForm((f) => ({ ...f, org_type: v }))}>
            <SelectTrigger data-testid="org-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ORG_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{localize(ORG_TYPES, o.value, lang)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>{t("organizer.org_contact_label")}</Label><Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="org-contact-input" /></div>
        <div><Label>{t("organizer.org_website_label")}</Label><Input value={form.org_website} onChange={(e) => setForm((f) => ({ ...f, org_website: e.target.value }))} data-testid="org-website-input" /></div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="org-save-btn">{saving ? t("common.saving") : t("common.save")}</Button>
    </div>
  );
}

export default function OrganizerDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const approved = user?.organizer_status === "approved";

  const load = () => {
    setLoading(true);
    api.get("/initiatives", { params: { mine: true } }).then((r) => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (approved) load(); else setLoading(false); }, [approved]);

  if (!approved) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("organizer.title")}</h1>
        <div className="mt-6"><EmptyState icon={Users} title={t("status.pending")} description={t("organizer.pending_account")} testId="organizer-pending" /></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("organizer.title")}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">{t("organizer.dashboard_subtitle")}</p>
        </div>
        <Button onClick={() => setCreating(true)} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="new-initiative-btn"><Plus className="mr-1.5 h-4 w-4" />{t("organizer.new_initiative")}</Button>
      </div>

      <div className="mt-10">
        <OrgProfileCard />
        <Tabs defaultValue="initiatives">
          <TabsList>
            <TabsTrigger value="initiatives" data-testid="org-tab-initiatives"><Users className="mr-1.5 h-4 w-4" />{t("nav.initiatives")}</TabsTrigger>
            <TabsTrigger value="inbox" data-testid="org-tab-inbox"><Inbox className="mr-1.5 h-4 w-4" />{t("inbox.nav")}</TabsTrigger>
          </TabsList>
          <TabsContent value="initiatives" className="mt-6">
            {loading ? <p className="text-muted-foreground">{t("common.loading")}</p>
            : list.length === 0 ? (
              <EmptyState icon={Users} title={t("organizer.no_initiatives")} testId="organizer-empty"
                action={<Button onClick={() => setCreating(true)} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="empty-create-btn"><Plus className="mr-1.5 h-4 w-4" />{t("organizer.new_initiative")}</Button>} />
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {list.map((i) => (
                  <Link key={i.id} to={`/organizador/iniciativa/${i.id}`} data-testid={`org-initiative-${i.id}`}
                    className="group flex flex-col rounded-2xl border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-terracotta/40 hover:shadow-[0_8px_30px_rgba(200,109,81,0.08)]">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-display text-lg font-semibold tracking-tight text-slateink">{i.name}</h3>
                      <StatusBadge status={i.status} label={t(`status.${i.status}`)} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {i.dates && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{i.dates}</span>}
                      {i.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{i.location}</span>}
                    </div>
                    <div className="mt-5 grid grid-cols-4 gap-2 border-t border-border pt-4 text-center">
                      {[[t("initiatives.goal"), i.participation_goal || "—"], [t("initiatives.applicants"), i.applicants], [t("initiatives.confirmed"), i.confirmed], [t("initiatives.remaining"), i.participation_goal ? i.remaining : "—"]].map(([l, v], idx) => (
                        <div key={idx}>
                          <p className="font-display text-lg font-bold text-slateink">{v}</p>
                          <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p>
                        </div>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-terracotta">{t("organizer.open_workspace")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="inbox" className="mt-6">
            <h2 className="mb-4 font-display text-lg font-semibold text-slateink">{t("inbox.title_org")}</h2>
            <InquiryInbox mode="sent" />
          </TabsContent>
        </Tabs>
      </div>

      <InitiativeEditor open={creating} onOpenChange={setCreating} initiative={null} onSaved={load} />
    </div>
  );
}
